import React, {useState, useRef, useCallback} from 'react';
import {View, StyleSheet, ScrollView, TouchableOpacity} from 'react-native';
import {Appbar, Text} from 'react-native-paper';
import {useTranslation} from 'react-i18next';
import {useNavigation, DrawerActions} from '@react-navigation/native';
import ReportDashboardTab from './ReportDashboardTab';
import ReportInsightsTab from './ReportInsightsTab';
import ReportMonthlyTab from './ReportMonthlyTab';
import ReportSalesTab from './ReportSalesTab';
import ReportPaymentsTab from './ReportPaymentsTab';
import ReportDuesTab from './ReportDuesTab';
import {useOrgUnitStore} from '../../store/useOrgUnitStore';
import OrgUnitSelector from '../../components/common/OrgUnitSelector';
import {colors, spacing} from '../../theme';

const TABS = [
  {key: 'dashboard', component: ReportDashboardTab},
  {key: 'insights', component: ReportInsightsTab},
  {key: 'monthly', component: ReportMonthlyTab},
  {key: 'sales', component: ReportSalesTab},
  {key: 'payments', component: ReportPaymentsTab},
  {key: 'dues', component: ReportDuesTab},
];

const TAB_LABEL_KEYS = {
  dashboard: 'report.tabDashboard',
  insights: 'report.tabInsights',
  monthly: 'report.tabMonthly',
  sales: 'report.tabSales',
  payments: 'report.tabPayments',
  dues: 'report.tabDues',
};

const ReportsScreen = () => {
  const {t} = useTranslation();
  const navigation = useNavigation();
  const {selectedOrgUnitId} = useOrgUnitStore();
  const [activeTab, setActiveTab] = useState('dashboard');
  const scrollRef = useRef(null);
  const tabRefs = useRef({});

  const handleTabPress = useCallback((key) => {
    setActiveTab(key);
    const ref = tabRefs.current[key];
    if (ref && scrollRef.current) {
      ref.measureLayout(
        scrollRef.current.getInnerViewRef(),
        (x) => {
          scrollRef.current.scrollTo({x: Math.max(0, x - 20), animated: true});
        },
        () => {},
      );
    }
  }, []);

  const ActiveComponent = TABS.find(tab => tab.key === activeTab)?.component;

  return (
    <View style={styles.container}>
      <Appbar.Header style={styles.appbar} dark>
        <Appbar.Action
          icon="menu"
          onPress={() => navigation.dispatch(DrawerActions.openDrawer())}
        />
        <Appbar.Content title={t('nav.reports')} />
        <OrgUnitSelector />
      </Appbar.Header>

      <View style={styles.tabBarContainer}>
        <ScrollView
          ref={scrollRef}
          horizontal
          showsHorizontalScrollIndicator={false}
          contentContainerStyle={styles.tabBarContent}>
          {TABS.map(tab => {
            const isActive = activeTab === tab.key;
            return (
              <TouchableOpacity
                key={tab.key}
                ref={ref => { tabRefs.current[tab.key] = ref; }}
                onPress={() => handleTabPress(tab.key)}
                style={[styles.tab, isActive && styles.tabActive]}
                activeOpacity={0.7}>
                <Text
                  style={[
                    styles.tabLabel,
                    isActive ? styles.tabLabelActive : styles.tabLabelInactive,
                  ]}>
                  {t(TAB_LABEL_KEYS[tab.key])}
                </Text>
              </TouchableOpacity>
            );
          })}
        </ScrollView>
      </View>

      <View style={styles.content}>
        {ActiveComponent && <ActiveComponent orgUnitId={selectedOrgUnitId} />}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {flex: 1, backgroundColor: colors.background},
  appbar: {backgroundColor: colors.primary},
  tabBarContainer: {
    backgroundColor: colors.surface,
    borderBottomWidth: 1,
    borderBottomColor: colors.divider,
  },
  tabBarContent: {
    paddingHorizontal: spacing.xs,
  },
  tab: {
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm + 2,
    borderBottomWidth: 3,
    borderBottomColor: 'transparent',
  },
  tabActive: {
    borderBottomColor: colors.primary,
  },
  tabLabel: {
    fontSize: 14,
    fontWeight: '600',
  },
  tabLabelActive: {
    color: colors.primary,
  },
  tabLabelInactive: {
    color: colors.textSecondary,
  },
  content: {flex: 1},
});

export default ReportsScreen;
